import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';

// Points configuration - 5% rule
export const POINTS_PER_EURO = 5; // 5 points per euro spent (5%)
export const POINTS_REDEMPTION_RATE = 100; // 100 points = 1€ discount

export interface LoyaltyTransaction {
  id: string;
  type: 'earned' | 'redeemed';
  points: number;
  description: string;
  date: string;
  order_id?: string;
}

interface LoyaltyStore {
  points: number;
  totalEarned: number;
  transactions: LoyaltyTransaction[];
  isLoading: boolean;
  isInitialized: boolean;
  
  // Actions
  initialize: (userId: string) => Promise<void>;
  refreshFromServer: (userId: string) => Promise<void>;
  getAvailableDiscount: () => number;
  calculatePointsForPurchase: (purchaseAmount: number) => number;
  reset: () => void;
}

export const useLoyaltyStore = create<LoyaltyStore>()((set, get) => ({
  points: 0,
  totalEarned: 0,
  transactions: [],
  isLoading: false,
  isInitialized: false,

  initialize: async (userId: string) => {
    if (get().isInitialized) return;
    
    set({ isLoading: true });
    
    try {
      // Use secure RPC function to initialize/get loyalty points
      const { data: loyaltyData, error: initError } = await supabase
        .rpc('initialize_loyalty_points', { p_user_id: userId });

      if (initError) {
        console.error('Error initializing loyalty points:', initError);
      }

      // Fetch transactions (read-only, users can only SELECT)
      const { data: transactionsData } = await supabase
        .from('loyalty_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      const transactions: LoyaltyTransaction[] = (transactionsData || []).map((tx: any) => ({
        id: tx.id,
        type: tx.type as 'earned' | 'redeemed',
        points: tx.points,
        description: tx.description,
        date: tx.created_at,
        order_id: tx.order_id,
      }));

      // Get the initialized points
      const pointsResult = loyaltyData?.[0];

      set({
        points: pointsResult?.points || 0,
        totalEarned: pointsResult?.total_earned || 0,
        transactions,
        isLoading: false,
        isInitialized: true,
      });
    } catch (error) {
      console.error('Error initializing loyalty store:', error);
      set({ isLoading: false });
    }
  },

  refreshFromServer: async (userId: string) => {
    try {
      // Fetch current points (read-only)
      const { data: loyaltyData } = await supabase
        .from('loyalty_points')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      // Fetch transactions
      const { data: transactionsData } = await supabase
        .from('loyalty_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      const transactions: LoyaltyTransaction[] = (transactionsData || []).map((tx: any) => ({
        id: tx.id,
        type: tx.type as 'earned' | 'redeemed',
        points: tx.points,
        description: tx.description,
        date: tx.created_at,
        order_id: tx.order_id,
      }));

      set({
        points: loyaltyData?.points || 0,
        totalEarned: loyaltyData?.total_earned || 0,
        transactions,
      });
    } catch (error) {
      console.error('Error refreshing loyalty data:', error);
    }
  },

  getAvailableDiscount: () => {
    const { points } = get();
    return Math.floor(points / POINTS_REDEMPTION_RATE);
  },

  calculatePointsForPurchase: (purchaseAmount: number) => {
    return Math.floor(purchaseAmount * POINTS_PER_EURO);
  },

  reset: () => {
    set({
      points: 0,
      totalEarned: 0,
      transactions: [],
      isLoading: false,
      isInitialized: false,
    });
  },
}));
